/**
 * La cloche de la barre de navigation, et son badge de non-lus.
 *
 * C'est aussi elle qui **ouvre le flux SSE** (`useEventStream`), parce qu'elle
 * est montée sur toutes les pages authentifiées : brancher le flux dans la page
 * des notifications ne le tiendrait ouvert que pendant qu'on la regarde,
 * c'est-à-dire précisément quand le temps réel ne sert à rien.
 *
 * Depuis la phase 10, ce flux **n'est plus le sien** : il porte aussi les
 * messages (docs/13 §5). La cloche continue de l'ouvrir parce qu'elle est le
 * composant toujours monté, et `MessagesLink` s'en abstient — un seul flux par
 * onglet, sans quoi le serveur tiendrait deux connexions pour les mêmes trames.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useUnreadCount } from "@/features/notifications/api/useNotifications";
import { useEventStream } from "@/shared/realtime/useEventStream";

export function NotificationBell() {
  const { t } = useTranslation("notifications");
  const { data: unread } = useUnreadCount();
  useEventStream();

  const count = unread ?? 0;

  return (
    <Link
      to="/notifications"
      className="relative inline-flex items-center rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      aria-label={t("bell.label")}
      data-testid="notification-bell"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-5 w-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0a3 3 0 1 1-6 0"
        />
      </svg>
      {count > 0 ? (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] leading-4 font-semibold text-white"
          data-testid="unread-badge"
        >
          {/* Au-delà de 99, la pastille déformerait la barre : on plafonne
              l'affichage, jamais le compteur lui-même. */}
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
      <span className="sr-only">{t("bell.unread", { unreadValue: count })}</span>
    </Link>
  );
}
